/**
 * Filtro de canal da lista de conversas: WhatsApp e Instagram conectados.
 */
import React, { useEffect, useState } from 'react';
import { AppIcon } from '../../../components/icons/AppIcon';
import { InstagramIcon, WhatsAppIcon } from '../../../components/icons/CustomIcons';
import { cn } from '@/lib/utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
} from '../../../components/ui/dropdown-menu-advanced';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { supabase } from '../../../lib/supabase';
import { useWhatsAppConnections } from '../../settings/auto-movement/hooks/useWhatsAppConnections';
import {
    getInstagramConnections,
    getInstagramDisplayHandle,
} from '../../../services/modules/instagramRepository';

interface ChannelOption {
    id: string;
    label: string;
    platform: 'whatsapp' | 'instagram';
}

interface ChannelInboxFilterProps {
    value: string | null;
    onChange: (connectionId: string | null) => void;
}

export const ChannelInboxFilter: React.FC<ChannelInboxFilterProps> = ({ value, onChange }) => {
    const { currentOrganization } = useOrganization();
    const { connections: whatsapp } = useWhatsAppConnections(currentOrganization?.id);
    const [instagram, setInstagram] = useState<ChannelOption[]>([]);
    const [cloud, setCloud] = useState<ChannelOption[]>([]);

    useEffect(() => {
        const organizationId = currentOrganization?.id;
        if (!organizationId) {
            setInstagram([]);
            return;
        }
        let cancelled = false;
        void getInstagramConnections(organizationId)
            .then((rows) => {
                if (cancelled) return;
                setInstagram(
                    rows
                        .filter((row) => row.connected && row.id)
                        .map((row) => ({
                            id: row.id as string,
                            platform: 'instagram' as const,
                            label: getInstagramDisplayHandle(row),
                        })),
                );
            })
            .catch(() => {
                if (!cancelled) setInstagram([]);
            });
        return () => {
            cancelled = true;
        };
    }, [currentOrganization?.id]);

    useEffect(() => {
        const organizationId = currentOrganization?.id;
        if (!organizationId) {
            setCloud([]);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const { data } = await supabase
                    .from('integrations')
                    .select('id, name, status, config')
                    .eq('organization_id', organizationId)
                    .eq('type', 'whatsapp_cloud')
                    .eq('status', 'connected')
                    .maybeSingle();
                if (cancelled || !data) {
                    if (!cancelled) setCloud([]);
                    return;
                }
                const display = data.config?.display_number;
                const number = typeof display === 'string' && display ? display : data.name;
                setCloud([{
                    id: data.id,
                    platform: 'whatsapp',
                    label: `WhatsApp Business · ${number}`,
                }]);
            } catch {
                if (!cancelled) setCloud([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentOrganization?.id]);

    const whatsappOptions: ChannelOption[] = whatsapp
        .filter((connection) => connection.integration_id)
        .map((connection) => ({
            id: connection.integration_id as string,
            platform: 'whatsapp',
            label: connection.phone_number || connection.instance_name,
        }));

    const selected = Boolean(value);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn('btn-icon toolbar-icon-btn shrink-0', selected && 'toolbar-icon-btn--active')}
                    aria-pressed={selected}
                    aria-label="Filtrar por canal"
                    title="Filtrar por canal"
                >
                    <AppIcon name="sliders" className="h-5 w-5 stroke-[1.5]" aria-hidden />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel>Canais conectados</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                    value={value ?? 'all'}
                    onValueChange={(next) => onChange(next === 'all' ? null : next)}
                >
                    <DropdownMenuRadioItem value="all">Todos os canais</DropdownMenuRadioItem>
                    {cloud.map((option) => (
                        <DropdownMenuRadioItem key={option.id} value={option.id} className="gap-2">
                            <WhatsAppIcon className="size-4 text-[#25D366]" />
                            <span className="truncate">{option.label}</span>
                        </DropdownMenuRadioItem>
                    ))}
                    {whatsappOptions.map((option) => (
                        <DropdownMenuRadioItem key={option.id} value={option.id} className="gap-2">
                            <WhatsAppIcon className="size-4 text-[#25D366]" />
                            <span className="truncate">{option.label}</span>
                        </DropdownMenuRadioItem>
                    ))}
                    {instagram.map((option) => (
                        <DropdownMenuRadioItem key={option.id} value={option.id} className="gap-2">
                            <InstagramIcon className="size-4" />
                            <span className="truncate">{option.label}</span>
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
                {whatsappOptions.length === 0 && cloud.length === 0 && instagram.length === 0 && (
                    <p className="px-2 py-1.5 text-sm text-muted">Nenhum canal conectado</p>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
